import { useCallback, useEffect, useState, useRef } from "react";
import { LinkContainer } from "react-router-bootstrap";
import { useSearchParams } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import Alert from "react-bootstrap/esm/Alert";
import Stack from "react-bootstrap/esm/Stack";

import { AppLayout } from "../components/appLayout";
import { AddBookmarkModal } from "../components/AddBookmarkModal";
import useApiClient from "../hooks/useApiClient";
import { BookmarkViewModel, BookmarkViewModelPaginatedResultsViewModel, CategoryWithCountsViewModel } from "../apiClient";
import { CategorySelect, CountType } from "../components/inputs/categorySelect";
import { uncategorizedText } from "../constants";
import { confirmAction } from "../helpers/alertHelper";
import { AppPagination } from "../components/appPagination";
import { AppSpinner } from "../components/appSpinner";
import { useQuickAdd } from "../contexts/QuickAddContext";

type SearchOptions = {
    page: number;
    query?: string;
    categoryName?: string;
}

type BulkProgress = {
    current: number;
    total: number;
    errors: number;
}

const PAGE_SIZE = 100;

const getSearchOptionsFromUrlParams = (urlSearchParams: URLSearchParams): SearchOptions => {
    const page = urlSearchParams.get('page') ? parseInt(urlSearchParams.get('page')!) : 1;
    const query = urlSearchParams.get('query') ?? undefined;
    const categoryName = urlSearchParams.get('categoryName') ?? undefined;
    return { page, query, categoryName };
};

const BookmarksHeader = ({
                             totalCount,
                             performingAction,
                             bulkImporting,
                             onAddBookmark,
                             onBulkImport,
                             hasResults
                         }: {
    totalCount?: number;
    performingAction: boolean;
    bulkImporting: boolean;
    onAddBookmark: () => void;
    onBulkImport: () => void;
    hasResults: boolean;
}) => (
    <Row className="align-items-center">
        <Col>
            <h1>Bookmarks ({totalCount})</h1>
        </Col>
        <Col xs="auto">
            <Button onClick={onAddBookmark} disabled={performingAction || bulkImporting}>Add bookmark</Button>
        </Col>
        <Col xs="auto">
            <LinkContainer to={"/bookmarks/add/bulk"}>
                <Button disabled={performingAction || bulkImporting}>Bulk add</Button>
            </LinkContainer>
        </Col>
        <Col xs="auto">
            <Button
                onClick={onBulkImport}
                disabled={performingAction || bulkImporting || !hasResults}
            >
                Import All
            </Button>
        </Col>
    </Row>
);

const SearchInput = ({
                         value,
                         onChange
                     }: {
    value: string;
    onChange: (query: string) => void;
}) => (
    <input
        type="text"
        className="form-control"
        placeholder="Search"
        value={value}
        onChange={e => onChange(e.target.value)}
    />
);

const BookmarksTable = ({
                            bookmarks,
                            performingAction,
                            bulkImporting,
                            onImport,
                            onRescan,
                            onDelete,
                            categories,
                            categoryValue,
                            onCategoryChange
                        }: {
    bookmarks: BookmarkViewModel[];
    performingAction: boolean;
    bulkImporting: boolean;
    onEdit: (bookmark: BookmarkViewModel) => void;
    onImport: (bookmark: BookmarkViewModel) => void;
    onRescan: (bookmark: BookmarkViewModel) => void;
    onDelete: (bookmark: BookmarkViewModel) => void;
    categories: CategoryWithCountsViewModel[];
    categoryValue: string;
    onCategoryChange: (name: string) => void;
}) => (
    <Table striped bordered hover responsive className="bookmarks-table">
        <thead>
        <tr>
            <th className="bookmarks-table__col bookmarks-table__col--name">Name</th>
            <th className="bookmarks-table__col bookmarks-table__col--category">
                <div style={{ maxWidth: 240, width: '100%' }}>
                    <CategorySelect
                        categories={categories}
                        onChange={onCategoryChange}
                        value={categoryValue}
                        name="bookmarkCategory"
                        countType={CountType.Bookmarks}
                        compact={true}
                        allLabel="All categories"
                    />
                </div>
            </th>
            <th className="bookmarks-table__col bookmarks-table__col--url">Url</th>
            <th className="bookmarks-table__col bookmarks-table__col--actions">Actions</th>
        </tr>
        </thead>
        <tbody>
        {bookmarks.map((bookmark) => (
            <tr key={bookmark.id}>
                <td className="bookmarks-table__col bookmarks-table__col--name">
                    {bookmark.name ?? bookmark.url}
                </td>
                <td className="bookmarks-table__col bookmarks-table__col--category">
                    {bookmark.categoryName ?? uncategorizedText}
                </td>
                <td className="bookmarks-table__col bookmarks-table__col--url">
                    <a href={bookmark.url!} rel="noreferrer" target="_blank">
                        {bookmark.url}
                    </a>
                </td>
                <td className="bookmarks-table__col bookmarks-table__col--actions">
                    <DropdownButton
                        id={`bookmark-actions-${bookmark.id}`}
                        title="Actions"
                        variant="secondary"
                        size="sm"
                        disabled={performingAction || bulkImporting}
                    >
                        <LinkContainer to={`/bookmarks/${bookmark.id}/edit`}>
                            <Dropdown.Item>Edit</Dropdown.Item>
                        </LinkContainer>
                        <Dropdown.Item onClick={() => onImport(bookmark)}>
                            Import
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => onRescan(bookmark)}>
                            Rescan
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => onDelete(bookmark)} className="text-danger">
                            Delete
                        </Dropdown.Item>
                    </DropdownButton>
                </td>
            </tr>
        ))}
        </tbody>
    </Table>
);

const BulkImportModal = ({
                             bulkProgress,
                             onCancel
                         }: {
    bulkProgress: BulkProgress;
    onCancel: () => void;
}) => (
    <div
        style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            flexDirection: "column",
            color: "white",
            textAlign: "center"
        }}
    >
        <AppSpinner/>
        <div style={{ marginTop: "1rem" }}>
            Importing {bulkProgress.current} of {bulkProgress.total}
            {bulkProgress.errors > 0 && (
                <div style={{ color: '#ff6b6b', marginTop: '0.5rem' }}>
                    Errors: {bulkProgress.errors}
                </div>
            )}
        </div>
        <Button variant="light" onClick={onCancel} style={{ marginTop: '1rem' }}>
            Cancel
        </Button>
    </div>
);

const useBulkImport = (
    apiClient: any,
    searchOptions: SearchOptions,
    loadData: () => Promise<void>
) => {
    const [bulkImporting, setBulkImporting] = useState(false);
    const [bulkProgress, setBulkProgress] = useState<BulkProgress>({
        current: 0,
        total: 0,
        errors: 0
    });
    const bulkCancelRef = useRef(false);

    const cancelBulkImport = useCallback(() => {
        bulkCancelRef.current = true;
        setBulkImporting(false);
    }, []);

    const performBulkImport = useCallback(async (totalCount: number) => {
        bulkCancelRef.current = false;
        setBulkImporting(true);

        let currentProgress = 0;
        let errorCount = 0;
        setBulkProgress({ current: currentProgress, total: totalCount, errors: errorCount });

        let currentPage = 1;

        while (true) {
            if (bulkCancelRef.current) break;

            const response = await apiClient?.bookmarksGET(currentPage, PAGE_SIZE, searchOptions.query, searchOptions.categoryName);
            if (!response?.results || response.results.length === 0) break;

            for (const bookmark of response.results) {
                if (bulkCancelRef.current) break;
                currentProgress++;

                try {
                    await apiClient?.importAsVideo(bookmark.id!);
                    setBulkProgress({ current: currentProgress, total: totalCount, errors: errorCount });
                } catch (e) {
                    console.error(e);
                    errorCount++;
                    setBulkProgress({ current: currentProgress, total: totalCount, errors: errorCount });
                }
            }

            if (bulkCancelRef.current || currentPage >= response.totalPages!) break;
            currentPage++;
        }

        setBulkImporting(false);
        if (!bulkCancelRef.current) {
            await loadData();
        }
    }, [apiClient, searchOptions.query, searchOptions.categoryName, loadData]);

    return {
        bulkImporting,
        bulkProgress,
        cancelBulkImport,
        performBulkImport
    };
};

const useBookmarkActions = (
    apiClient: any,
    loadData: () => Promise<void>
) => {
    const [performingAction, setPerformingAction] = useState(false);

    const deleteBookmark = useCallback(async (bookmark: BookmarkViewModel) => {
        if (!await confirmAction()) return;
        setPerformingAction(true);

        try {
            await apiClient?.bookmarksDELETE(bookmark.id!);
            await loadData();
        } finally {
            setPerformingAction(false);
        }
    }, [apiClient, loadData]);

    const importBookmark = useCallback(async (bookmark: BookmarkViewModel) => {
        setPerformingAction(true);

        try {
            await apiClient?.importAsVideo(bookmark.id!);
            await loadData();
        } finally {
            setPerformingAction(false);
        }
    }, [apiClient, loadData]);

    const rescanBookmark = useCallback(async (bookmark: BookmarkViewModel) => {
        setPerformingAction(true);

        try {
            await apiClient?.rescanTitle(bookmark.id!);
            await loadData();
        } finally {
            setPerformingAction(false);
        }
    }, [apiClient, loadData]);

    return {
        performingAction,
        deleteBookmark,
        importBookmark,
        rescanBookmark
    };
};

export const Bookmarks = () => {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const [categories, setCategories] = useState<CategoryWithCountsViewModel[]>([]);
    const [paginatedResults, setPaginatedResults] = useState<BookmarkViewModelPaginatedResultsViewModel>();
    const [searchOptions, setSearchOptions] = useState<SearchOptions>(
        getSearchOptionsFromUrlParams(urlSearchParams)
    );
    const [loading, setLoading] = useState<boolean>(true);
    const [showAddBookmarkModal, setShowAddBookmarkModal] = useState<boolean>(false);
    const apiClient = useApiClient();
    const { registerRefreshHandler } = useQuickAdd();

    const loadData = useCallback(async () => {
        if (!apiClient) return;
        setLoading(true);
        const bookmarks = await apiClient.bookmarksGET(searchOptions.page, PAGE_SIZE, searchOptions.query, searchOptions.categoryName);
        setPaginatedResults(bookmarks);
        setLoading(false);
    }, [apiClient, searchOptions]);

    const { performingAction, deleteBookmark, importBookmark, rescanBookmark } = useBookmarkActions(
        apiClient,
        loadData
    );

    const { bulkImporting, bulkProgress, cancelBulkImport, performBulkImport } = useBulkImport(
        apiClient,
        searchOptions,
        loadData
    );

    const [allCategories, setAllCategories] = useState<CategoryWithCountsViewModel[]>([]);

    useEffect(() => {
        if (!apiClient) return;
        apiClient.categoriesAll().then(c => {
            const all = c || [];
            setAllCategories(all);
            const filtered = all.filter(cat => (cat.bookmarkCount ?? 0) > 0);
            setCategories(filtered);
            // If the currently selected category isn't in the filtered list, clear it
            if (searchOptions.categoryName && !filtered.some(f => f.name === searchOptions.categoryName)) {
                setSearchOptions(prev => ({ ...prev, categoryName: undefined, page: 1 }));
            }
        }).catch(err => console.error(err));
    }, [apiClient]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        const unregister = registerRefreshHandler('bookmarks', loadData);
        return () => unregister();
    }, [registerRefreshHandler, loadData]);

    useEffect(() => {
        setUrlSearchParams(prev => {
            prev.set('page', searchOptions.page?.toString() ?? '1');
            prev.set('query', searchOptions.query ?? '');
            prev.set('categoryName', searchOptions.categoryName ?? '');
            return prev;
        }, { replace: true });
    }, [searchOptions, setUrlSearchParams]);

    const handlePageChange = useCallback((pageNumber: number) => {
        setSearchOptions(prev => ({ ...prev, page: pageNumber }));
    }, []);

    const handleSearchChange = useCallback((query: string) => {
        setSearchOptions(prev => ({ ...prev, query, page: 1 }));
    }, []);

    const handleCategoryChange = useCallback((categoryName: string) => {
        setSearchOptions(prev => ({ ...prev, categoryName: categoryName || undefined, page: 1 }));
    }, []);

    const handleBulkImport = useCallback(async () => {
        if (!paginatedResults?.results || paginatedResults.results.length === 0) return;
        await performBulkImport(paginatedResults.totalCount ?? 0);
    }, [paginatedResults, performBulkImport]);

    const hasResults = (paginatedResults?.results?.length ?? 0) > 0;

    return (
        <AppLayout loading={!paginatedResults && loading}>
            <Container fluid>
                {paginatedResults && (
                    <Stack gap={2}>
                        <BookmarksHeader
                            totalCount={paginatedResults.totalCount}
                            performingAction={performingAction}
                            bulkImporting={bulkImporting}
                            onAddBookmark={() => setShowAddBookmarkModal(true)}
                            onBulkImport={handleBulkImport}
                            hasResults={hasResults}
                        />

                        <SearchInput
                            value={searchOptions.query ?? ''}
                            onChange={handleSearchChange}
                        />

                        {loading && <AppSpinner/>}

                        {!loading && (
                            <>
                                {paginatedResults.totalCount === 0 && (
                                    <Alert variant="info">
                                        No bookmarks found.
                                    </Alert>
                                )}

                                {paginatedResults.totalCount! > 0 && (
                                    <BookmarksTable
                                        bookmarks={paginatedResults.results!}
                                        performingAction={performingAction}
                                        bulkImporting={bulkImporting}
                                        onEdit={() => {
                                        }}
                                        onImport={importBookmark}
                                        onRescan={rescanBookmark}
                                        onDelete={deleteBookmark}
                                        categories={categories}
                                        categoryValue={searchOptions.categoryName ?? ''}
                                        onCategoryChange={handleCategoryChange}
                                    />
                                )}

                                <div className="d-flex justify-content-center">
                                    <AppPagination
                                        page={paginatedResults.page!}
                                        pageSize={paginatedResults.pageSize!}
                                        totalPages={paginatedResults.totalPages!}
                                        totalCount={paginatedResults.totalCount!}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </>
                        )}
                    </Stack>
                )}
            </Container>

            {bulkImporting && (
                <BulkImportModal
                    bulkProgress={bulkProgress}
                    onCancel={cancelBulkImport}
                />
            )}

            {apiClient && (
                <AddBookmarkModal
                    show={showAddBookmarkModal}
                    onHide={() => setShowAddBookmarkModal(false)}
                    apiClient={apiClient}
                    categories={allCategories}
                    onSuccess={loadData}
                />
            )}
        </AppLayout>
    );
};